@use '@/styles/variables' as *;

.page {
  min-height: 100vh;
  background: var(--background);
}

.main {
  max-width: 680px;
  margin: 0 auto;
  padding: 3.5rem 2rem 6rem;
}

.back {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  padding: 0;
  margin-bottom: 2.5rem;
  color: var(--muted-foreground);
  font: inherit;
  font-size: 0.875rem;
  cursor: pointer;
  transition: color 0.2s ease;

  &:hover {
    color: var(--foreground);
  }

  &:focus-visible {
    outline: 2px solid var(--foreground);
    outline-offset: 3px;
    border-radius: 3px;
  }
}

.notFound {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 4rem;
  color: var(--muted-foreground);
}

.articleMeta {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-bottom: 1.75rem;
}

.category {
  display: inline-block;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--forest);
  border: 1px solid var(--forest);
  border-radius: 999px;
  padding: 0.15rem 0.6rem;
}

.date {
  color: var(--muted-foreground);
  font-size: 0.82rem;
}

.readTime {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  color: var(--muted-foreground);
  font-size: 0.82rem;
}

.title {
  font-size: clamp(2rem, 5vw, 3rem);
  font-weight: 700;
  font-family: var(--font-display);
  letter-spacing: -0.03em;
  line-height: 1.15;
  margin-bottom: 1.25rem;
  text-wrap: balance;
  color: var(--foreground);
}

.lead {
  font-size: 1.05rem;
  color: var(--forest);
  line-height: 1.75;
}

.mdBody {
  margin-top: 0.5rem;
}

.sectionHeading {
  font-size: 1.3rem;
  font-weight: 700;
  font-family: var(--font-display);
  letter-spacing: -0.02em;
  margin: 2.25rem 0 0.75rem;
  color: var(--foreground);

  &:first-child {
    margin-top: 0;
  }
}

.sectionSubheading {
  font-size: 1.05rem;
  font-weight: 600;
  font-family: var(--font-display);
  letter-spacing: -0.01em;
  margin: 1.5rem 0 0.5rem;
  color: var(--foreground);
}

.sectionBody {
  font-size: 0.975rem;
  color: var(--muted-foreground);
  line-height: 1.8;
  margin-bottom: 0.75rem;
}

.codeInline {
  font-family: var(--font-mono, 'Fira Code', 'Cascadia Code', monospace);
  font-size: 0.875em;
  background: color-mix(in oklch, var(--muted) 60%, var(--background));
  color: var(--forest);
  border-radius: 4px;
  padding: 0.15em 0.4em;
}

.bullets {
  list-style: disc;
  padding-left: 1.25rem;
  font-size: 0.975rem;
  color: var(--muted-foreground);
  line-height: 1.75;
  margin-bottom: 0.75rem;

  li {
    margin-bottom: 0.3rem;
  }
}

.numberedList {
  list-style: decimal;
  padding-left: 1.25rem;
  font-size: 0.975rem;
  color: var(--muted-foreground);
  line-height: 1.75;
  margin-bottom: 0.75rem;

  li {
    margin-bottom: 0.3rem;
  }
}

.mdDivider {
  border: none;
  border-top: 1px solid var(--border);
  margin: 1.75rem 0;
}

.mdLink {
  color: var(--forest);
  text-decoration: underline;
  text-underline-offset: 3px;

  &:hover {
    opacity: 0.8;
  }
}

.likeRow {
  display: flex;
  justify-content: center;
  margin: 3rem 0 0;
}

.likeBtn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  background: none;
  border: 1px solid var(--border);
  border-radius: 999px;
  padding: 0.5rem 1.1rem;
  color: var(--muted-foreground);
  font: inherit;
  font-size: 0.875rem;
  cursor: pointer;
  transition:
    color 0.2s ease,
    border-color 0.2s ease;

  svg {
    transition: transform 0.15s ease;
  }

  &:hover:not(:disabled) {
    color: var(--foreground);
    border-color: var(--foreground);

    svg {
      transform: scale(1.15);
    }
  }

  &:focus-visible {
    outline: 2px solid var(--foreground);
    outline-offset: 3px;
  }
}

.likeBtnActive {
  color: #e05252;
  border-color: #e05252;
  cursor: default;

  svg {
    fill: #e05252;
    stroke: #e05252;
  }
}

.divider {
  border: none;
  border-top: 1px solid var(--border);
  margin: 3rem 0 2rem;
}

.keepReading {
  // intentional wrapper — no decoration needed
}

.keepReadingLabel {
  font-size: 0.7rem;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--muted-foreground);
  margin-bottom: 1.5rem;
}

.relatedList {
  list-style: none;
  padding: 0;
  margin: 0;
  border-top: 1px solid var(--border);
}

.relatedItem {
  border-bottom: 1px solid var(--border);
}

.relatedBtn {
  width: 100%;
  background: none;
  border: none;
  padding: 1.25rem 0;
  text-align: left;
  cursor: pointer;
  font: inherit;

  &:hover .relatedTitle {
    color: var(--forest);
  }

  &:focus-visible {
    outline: 2px solid var(--forest);
    outline-offset: 3px;
    border-radius: 3px;
  }
}

.relatedMeta {
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.4rem;
}

.relatedTitle {
  font-size: 1rem;
  font-weight: 600;
  font-family: var(--font-display);
  letter-spacing: -0.02em;
  color: var(--foreground);
  transition: color 0.2s ease;
}

@media (max-width: #{$breakpoint-sm}) {
  .main {
    padding: 2.5rem 1.25rem 4rem;
  }
}

@media (prefers-reduced-motion: reduce) {
  .back,
  .relatedTitle {
    transition: none;
  }
}
